import React, { isValidElement, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import NextButton from '../shared/NextButton';
import Password from '../shared/Password';

import './ChangePassword.scss';
import { Box, Dialog, DialogContent, DialogTitle, Typography, Button } from '@mui/material';

function ChangePassword({ callback, disabled, usingCurrentPassword = false, nextButton = true, extraButtons }) {
    const [t] = useTranslation();
    const passwordRef = useRef();
    const currentPasswordRef = useRef();
    const confirmPasswordRef = useRef();

    const [passwordBlank, setPasswordBlank] = useState(true);
    const [confirmPasswordBlank, setConfirmPasswordBlank] = useState(true);
    const [currentPasswordBlank, setCurrentPasswordBlank] = useState(usingCurrentPassword);
    const [matchFailure, setMatchFailure] = useState(false);
    const [complexityFailure, setComplexityFailure] = useState(false);
    const [complexityFailureMsg, setComplexityFailureMsg] = useState('');

    /**Returns reason this password does not meet complexity requirements.
     * @param {string} pwd Password to examine.
     * @returns {string|false} Failure reason, if there is one.
     */
    const complexityFailureReason = (pwd) => {
        if (!/\d/.test(pwd)) return t('Authentication.CreateAccount.EnterPasswordNumber');
        if (!/[~!@#$%^&]/.test(pwd)) return t('Authentication.CreateAccount.EnterPasswordSymbol');
        if (!/.{8}/.test(pwd)) return t('Authentication.CreateAccount.EnterPasswordLength');
        if (!/[A-Z]/.test(pwd)) return t('Authentication.CreateAccount.EnterUppercase');
        if (!/[a-z]/.test(pwd)) return t('Authentication.CreateAccount.EnterLowercase');

        return false;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setComplexityFailure(false);
        setComplexityFailureMsg('');
        setMatchFailure(false);

        if (passwordRef.current?.value !== confirmPasswordRef.current?.value) {
            setMatchFailure(true);
            toast.error(t('Authentication.CreateAccount.PasswordsNotMatch'));
            return;
        }

        var failReason = complexityFailureReason(passwordRef.current?.value);
        if (failReason) {
            setComplexityFailure(true);
            setComplexityFailureMsg(failReason);
            toast.error(failReason);
            return;
        }

        usingCurrentPassword
            ? callback(currentPasswordRef.current?.value, passwordRef.current?.value)
            : callback(passwordRef.current?.value);
    };

    return (
        <Dialog open={true}>
            <DialogTitle>{t('Authentication.CreateNewPassword')}</DialogTitle>
            <DialogContent>
                <Box>
                    <Typography>
                        {usingCurrentPassword
                            ? t('Authentication.ChangePasswordRequirements')
                            : t('Authentication.ForgotPasswordRequirements')}
                    </Typography>

                    {usingCurrentPassword ? (
                        <Password
                            className="current-password"
                            innerRef={currentPasswordRef}
                            labelText={t('Authentication.CurrentPassword')}
                            placeholder={t('Authentication.EnterPassword')}
                            disabled={disabled}
                            autofocus={true}
                            onChange={(val) => setCurrentPasswordBlank(val.length === 0)}
                        />
                    ) : null}
                    <Password
                        innerRef={passwordRef}
                        className={!complexityFailure ? 'passing' : null}
                        labelText={t('Authentication.NewPassword')}
                        placeholder={t('Authentication.EnterPassword')}
                        disabled={disabled}
                        autofocus={usingCurrentPassword ? false : true}
                        onChange={(val) => setPasswordBlank(val.length === 0)}
                    />
                    {complexityFailure ? <div id="instruction-password">{complexityFailureMsg}</div> : null}
                    <Password
                        innerRef={confirmPasswordRef}
                        className={!matchFailure ? 'passing' : null}
                        labelText={t('Authentication.ConfirmPassword')}
                        placeholder={t('Authentication.EnterPassword')}
                        disabled={disabled}
                        onChange={(val) => setConfirmPasswordBlank(val.length === 0)}
                    />
                    {matchFailure ? <div id="instruction-confirm-password">{t('Authentication.ConfirmPasswordInstructions')}</div> : null}
                </Box>
                <Box>
                    {extraButtons?.every?.((button) => isValidElement(button)) ? extraButtons : null}
                    {nextButton ? (
                        <NextButton
                            action={handleSubmit}
                            disabled={disabled || currentPasswordBlank || passwordBlank || confirmPasswordBlank}
                        >
                            {usingCurrentPassword ? t('Authentication.ChangePassword') : t('Authentication.ResetPassword')}
                        </NextButton>
                    ) : (
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={disabled || currentPasswordBlank || passwordBlank || confirmPasswordBlank}
                        >
                            {usingCurrentPassword ? t('Authentication.ChangePassword') : t('Authentication.ResetPassword')}
                        </Button>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default ChangePassword;
